import { useEffect, useState } from 'react'

export interface Gif {
  id: string
  gif: string
  preview: string
  w: number
  h: number
}

interface GifPickerProps {
  onSelect: (gif: Gif) => void
  onClose: () => void
}

export function GifPicker({ onSelect, onClose }: GifPickerProps) {
  const [q, setQ] = useState('')
  const [gifs, setGifs] = useState<Gif[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true)
      setError(null)
      fetch(`/api/gifs${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`)
        .then((r) => r.json())
        .then((d: { gifs?: Gif[]; error?: string }) => {
          if (d.error) { setError(d.error); setGifs([]) } else setGifs(d.gifs ?? [])
        })
        .catch(() => setError('network'))
        .finally(() => setLoading(false))
    }, 300)
    return () => clearTimeout(t)
  }, [q])

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute bottom-full mb-2 left-0 z-50 w-80 h-96 flex flex-col bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg shadow-lg overflow-hidden">
        <div className="p-2 border-b border-[rgb(var(--np-border))]">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search GIFs…"
            className="w-full bg-[rgb(var(--np-raised))] rounded-md px-3 py-1.5 text-sm text-[rgb(var(--np-tx-primary))] placeholder-[rgb(var(--np-tx-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--np-accent)/0.5)]"
          />
        </div>
        <div className="scroll flex-1 overflow-y-auto p-1.5">
          {error === 'no_api_key' ? (
            <p className="text-xs text-[rgb(var(--np-tx-muted))] p-4 text-center leading-relaxed">
              GIF search isn't configured.<br />Add <code className="font-data">GIPHY_API_KEY</code> to the server.
            </p>
          ) : error ? (
            <p className="text-xs text-[rgb(var(--np-tx-muted))] p-4 text-center">Couldn't load GIFs.</p>
          ) : loading && gifs.length === 0 ? (
            <p className="text-xs text-[rgb(var(--np-tx-muted))] p-4 text-center">Loading…</p>
          ) : gifs.length === 0 ? (
            <p className="text-xs text-[rgb(var(--np-tx-muted))] p-4 text-center">No GIFs found.</p>
          ) : (
            <div className="columns-2 gap-1.5">
              {gifs.map((g) => (
                <button
                  key={g.id}
                  onClick={() => onSelect(g)}
                  className="mb-1.5 block w-full overflow-hidden rounded-md hover:ring-2 hover:ring-[rgb(var(--np-accent))] transition-shadow"
                >
                  <img src={g.preview} alt="" loading="lazy" className="w-full block bg-[rgb(var(--np-raised))]" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="px-2 py-1 border-t border-[rgb(var(--np-border))] text-[9px] font-data text-[rgb(var(--np-tx-muted))] text-right">
          Powered by GIPHY
        </div>
      </div>
    </>
  )
}
